import { Calendar, MessageSquare, UploadCloud, Activity, Users, AlertCircle } from 'lucide-react';
import { useMemo } from 'react';

import { KpiCard } from '../../components/dashboard/KpiCard';
import { MAIL_FAILURES_ORIGIN } from '../mailfailures/MailFailuresPage';
import { useDocumentsContext } from '../../providers/DocumentsProvider';
import { useHandoffStatsSummaryQuery } from '../../hooks/useHandoffStat';
import { useHealthStatus } from '../../hooks/useHealthStatus';
import { UseConverstionStats } from 'hooks/useConversationStats';
import { aggregateConversationStats } from 'utils/aggregationStat';

export const DashboardPage = () => {
  const {
    documents,
    isLoading: documentsLoading,
    isFetching: documentsFetching,
    error: documentsError,
  } = useDocumentsContext();
  const { loading, stat } = UseConverstionStats();
  const { data: handoffSummary } = useHandoffStatsSummaryQuery();
  const { health } = useHealthStatus();
  const aggregated = useMemo(() => aggregateConversationStats(stat), [stat]);

  const website = aggregated.byChannel.website;
  const totals = aggregated.total;

  const conversationCards = [
    {
      key: 'total-24h',
      label: 'Konversationer (24h)',
      value: totals.last24h,
      description: 'Totalt antal inkommande konversationer senaste 24 timmarna.',
      icon: <MessageSquare className="h-6 w-6" />,
    },
    {
      key: 'total-7d',
      label: 'Konversationer (7 dagar)',
      value: totals.last7Days,
      description: 'Totalt antal inkommande konversationer senaste veckan.',
      icon: <MessageSquare className="h-6 w-6" />,
    },
    {
      key: 'total-30d',
      label: 'Konversationer (30 dagar)',
      value: totals.last30Days,
      description: 'Totalt antal inkommande konversationer senaste månaden.',
      icon: <Calendar className="h-6 w-6" />,
    },
    {
      key: 'total-ytd',
      label: 'Konversationer (YTD)',
      value: totals.yearToDate ?? totals.last30Days,
      description: 'Totalt antal konversationer hittills i år.',
      icon: <MessageSquare className="h-6 w-6" />,
    },
  ];

  const channelCards = [
    {
      key: 'website-7d',
      label: 'Webbchat (7 dagar)',
      value: website?.last7Days ?? 0,
      description: 'Inkommande webbchat-konversationer senaste veckan.',
      icon: <MessageSquare className="h-6 w-6" />,
    },
    {
      key: 'website-30d',
      label: 'Webbchat (30 dagar)',
      value: website?.last30Days ?? 0,
      description: 'Inkommande webbchat-konversationer senaste månaden.',
      icon: <MessageSquare className="h-6 w-6" />,
    },
    {
      key: 'website-ytd',
      label: 'Webbchat (YTD)',
      value: website?.yearToDate ?? 0,
      description: 'Webbchat-konversationer hittills i år.',
      icon: <Calendar className="h-6 w-6" />,
    },
  ];

  const mailFailures7d = handoffSummary?.mailFailures?.last7Days ?? 0;

  const handoffCards = [
    {
      key: 'handoff-7d',
      label: 'Handoff begärd (7 dagar)',
      value: handoffSummary?.total?.last7Days ?? 0,
      description: 'Konversationer där kunden begärt handoff senaste veckan.',
      icon: <Users className="h-6 w-6" />,
    },
    {
      key: 'handoff-30d',
      label: 'Handoff begärd (30 dagar)',
      value: handoffSummary?.total?.last30Days ?? 0,
      description: 'Konversationer där kunden begärt handoff senaste månaden.',
      icon: <Users className="h-6 w-6" />,
    },
    {
      key: 'handoff-ytd',
      label: 'Handoff begärd (YTD)',
      value: handoffSummary?.total?.yearToDate ?? 0,
      description: 'Konversationer där kunden begärt handoff hittills i år.',
      icon: <Users className="h-6 w-6" />,
    },
    {
      key: 'mail-failures-7d',
      label: 'Handoff-mejl ej skickade (7 dagar)',
      value: mailFailures7d,
      description:
        mailFailures7d > 0
          ? 'Kunder har lovats svar utan att kundservice fått ärendet. Klicka för att se vilka.'
          : 'Alla handoff-mejl har gått fram till kundservice.',
      icon:
        mailFailures7d > 0 ? <AlertCircle className="h-6 w-6" /> : <Users className="h-6 w-6" />,
      tone: mailFailures7d > 0 ? ('danger' as const) : ('default' as const),
      to: mailFailures7d > 0 ? '/fastnade-mejl' : undefined,
      linkState: mailFailures7d > 0 ? MAIL_FAILURES_ORIGIN : undefined,
    },
  ];

  const knowledgeCards = [
    {
      key: 'documents',
      label: 'Dokument i kunskapsbas',
      value: documentsLoading || documentsFetching ? 'Laddar...' : documents.length,
      description: documentsError
        ? `Kunde inte ladda dokument: ${documentsError.message}`
        : 'Antal aktiva dokument i kunskapsbasen.',
      icon: <UploadCloud className="h-6 w-6" />,
    },
  ];

  const firstError = Object.values(health?.services ?? {}).find((service) => service?.error)?.error;

  const systemCards = [
    {
      key: 'latest-op',
      label: 'Senaste aktivitet om system.',
      value: health?.timestamp
        ? new Date(health.timestamp).toLocaleTimeString('sv-SE')
        : 'Laddar...',
      description: 'Senaste hälsokontroll från backend.',
      icon: <MessageSquare className="h-6 w-6" />,
    },
    {
      key: 'system-status',
      label: 'Systemstatus',
      value: health?.status ?? 'Okänd',
      description:
        health?.services?.orderTracking?.healthy &&
        health?.services?.catalog?.healthy &&
        health?.services?.outfitProfile?.healthy &&
        health?.services?.purchaseOrder?.healthy
          ? 'Alla tjänster fungerar korrekt.'
          : 'Varning: En eller flera tjänster är degraderade.',
      icon:
        health?.status === 'Operationell' ? (
          <Activity className="h-6 w-6" />
        ) : (
          <AlertCircle className="h-6 w-6" />
        ),
    },
    {
      key: 'integrations',
      label: 'Integrationer',
      value:
        (health?.services?.orderTracking?.healthy ? 1 : 0) +
        (health?.services?.catalog?.healthy ? 1 : 0) +
        (health?.services?.outfitProfile?.healthy ? 1 : 0) +
        (health?.services?.purchaseOrder?.healthy ? 1 : 0) +
        ' / 4',
      description: 'Orderspårning, katalog, stilprofil och inkommande lager.',
      icon: <Activity className="h-6 w-6" />,
    },
    ...(firstError
      ? [
          {
            key: 'system-error',
            label: 'Systemfel',
            value: firstError.code || 'Error',
            description: `${firstError.message}`,
            icon: <AlertCircle className="h-6 w-6" />,
          },
        ]
      : []),
  ];

  if (loading) {
    return (
      <section className="space-y-6">
        <header>
          <h2 className="text-3xl font-semibold text-brand-blue font-display">Översikt</h2>
          <p className="text-sm text-text-grey">Laddar statistik...</p>
        </header>
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="animate-pulse bg-gray-200 h-32 rounded-lg" />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-6">
      <header>
        <h2 className="text-3xl font-semibold text-brand-blue font-display">Översikt</h2>
        <p className="text-sm text-text-grey">
          En helhetsvy över agentens prestanda, konversationsvolymer och systemstatus i realtid.
        </p>
      </header>

      <div>
        <h3 className="text-lg font-semibold text-brand-blue mb-3">Konversationer totalt</h3>
        <div className="grid gap-4 md:grid-cols-4">
          {conversationCards.map((card) => (
            <KpiCard
              key={card.key}
              label={card.label}
              value={card.value}
              description={card.description}
              icon={card.icon}
            />
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-brand-blue mb-3">Per kanal</h3>
        <div className="grid gap-4 md:grid-cols-3">
          {channelCards.map((card) => (
            <KpiCard
              key={card.key}
              label={card.label}
              value={card.value}
              description={card.description}
              icon={card.icon}
            />
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-brand-blue mb-3">Handoff</h3>
        <div className="grid gap-4 md:grid-cols-4">
          {handoffCards.map((card) => (
            <KpiCard
              key={card.key}
              label={card.label}
              value={card.value}
              description={card.description}
              icon={card.icon}
              tone={card.tone}
              to={card.to}
              linkState={card.linkState}
            />
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-brand-blue mb-3">Systemstatus</h3>
        <div className="grid gap-4 md:grid-cols-4">
          {[...knowledgeCards, ...systemCards].map((card) => (
            <KpiCard
              key={card.key}
              label={card.label}
              value={card.value}
              description={card.description}
              icon={card.icon}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
